<template>
  <div class="full-width">
    <div
      ref="dataViewRow" class="row" :class="{ 'row-selected': props.isSelected }" :style="`grid-template-columns: ${columnsGridTemplate(props.columns)}`"
      @click="handleRowClick"
    >
      <div class="head first" :class="shouldHideOnBreakpoint(props.columns, 'title')">
        <VideoComponent
          remove-add-button
          is-cover
          :item="video"
          :hover-animation="false"
          :with-title-events="false"
          :enable-click-action="false"
          :show-time="false"
          :show-ownership="false"
          :show-top-text="false"
          :show-ai-generated-tag="false"
          :show-orientation="false"
          :show-new-label="false"
          class="video-component"
        />
        <div class="video-title">
          <NewTooltip :placement="'top'" :content="video.title" auto-ellipsis />
        </div>
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'duration_in_ms')" class="head">
        {{ formatDuration(video.duration_in_ms) }}
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'is_vertical')" class="head">
        <VideoLayoutOrientation :is-vertical="video.is_vertical" :tag-colors="TagColors.White" :size="TagSizes.Big" show-img />
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'ai_generated_article_url')" class="head url-cell">
        <NewTooltip v-if="aiGeneratedArticleUrl && isPairedToPage" :placement="'top'" :content="aiGeneratedArticleUrl" auto-ellipsis />
        <span v-else-if="aiGeneratedArticleUrl" class="url-disabled">{{ aiGeneratedArticleUrl }}</span>
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'is_paired_to_page')" class="head">
        <Label v-if="isPending" :text="$t('video-paired-url-pending-approval')" :color="LabelColors.Yellow" :size="LabelSizes.Large" />
        <Label v-else-if="isPairedToPage" :text="$t('video-paired-to-page')" :color="LabelColors.Green" :size="LabelSizes.Large" />
        <Label v-else :text="$t('video-unpaired')" :color="LabelColors.GreyWhite" :size="LabelSizes.Large" />
      </div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'upload_date')">{{ getDateString(video.upload_date) }}</div>

      <div :class="shouldHideOnBreakpoint(props.columns, 'actions')" class="head actions-cell">
        <Label
          v-if="isPending"
          class="approve-btn"
          :text="$t('approve')"
          :color="LabelColors.Blue"
          :size="LabelSizes.Medium"
          @click.stop="handleRowClick"
        />
        <ActionButtons v-else :actions="actions" :is-visible="isRowHovered" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useElementHover } from '@vueuse/core'
import { getDateString, formatDuration, debounce } from '@/services/utils'
import VideoComponent from '@/components/base/VideoComponent.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { ListColumn, VideoEditVideoSettingsScreen } from '@/interfaces'
import { VideoLayoutOrientation } from '@/components/common'
import { TagColors, TagSizes } from '@/components/common/tag/utils'
import NewTooltip from '@/components/common/tooltips/NewTooltip.vue'
import Label from '@/components/common/label/Label.vue'
import { LabelColors, LabelSizes } from '@/components/common/label/utils'
import { useSelectedAiVideo } from '@/composables/useSelectedAiVideo'
import { useDownloadVideo } from '@/composables/useDownloadVideo'
import type { Action } from '@/components/common/action-buttons/ActionButtons.vue'
import type { Video } from '@/services/videos/types'
import type { AiProposal } from '@/services/ai-generated/types'

const props = defineProps<{
  columns: ListColumn[]
  item: Video | AiProposal
  isSelected?: boolean
}>()

const router = useRouter()
const { setAiVideoPrefetch, refreshAiVideoPrefetch } = useSelectedAiVideo()

watch(() => props.item, (item) => refreshAiVideoPrefetch(item))

const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)
const { isDownloading, canDownload, download } = useDownloadVideo()

const isPending = computed(() => 'proposal_id' in props.item)

const video = computed<Video>(() => {
  if (isPending.value) {
    return (props.item as AiProposal).video
  }
  return props.item as Video
})

const isPairedToPage = computed(() => video.value.is_paired_to_page === true)
const aiGeneratedArticleUrl = computed(() => video.value.ai_generated_article_url ?? null)
const actions = computed<Action[]>(() => {
  const relevantActions: Action[] =[
    {
      label: 'pairedUrl',
      handler: openPairUrlTab,
      translationKey: 'library-embed-attach'
    }
  ]

  if (canDownload.value) {
    relevantActions.push({
      label: 'download',
      handler: () => download(video.value),
      translationKey: 'download-video',
      isLoading: isDownloading.value
    })
  }

  return relevantActions
})

const proposalIdQuery = computed(() => (isPending.value ? { proposal_id: (props.item as AiProposal).proposal_id } : {}))

const handleRowClick = debounce(() => {
  setAiVideoPrefetch(props.item)
  router.push({
    name: 'ai-generated-video-edit',
    params: { id: video.value.video_id },
    query: { tab: VideoEditVideoSettingsScreen.EditVideo, ...proposalIdQuery.value }
  })
}, 500)

function openPairUrlTab() {
  setAiVideoPrefetch(props.item)
  router.push({
    name: 'ai-generated-video-edit',
    params: { id: video.value.video_id },
    query: { tab: VideoEditVideoSettingsScreen.PairedUrls, ...proposalIdQuery.value }
  })
}
</script>

<style scoped lang="scss">
.row {
  width: 100%;
  height: 93px;
  display: grid;
  align-items: center;
  justify-items: start;

  > div {
    padding: 1rem;
    width: 100%;
  }

  &:hover {
    background-color: $rowSelectedBackground;
    cursor: pointer;
  }

  &.row-selected {
    background-color: $rowSelectedBackground;
  }

  .head {
    display: flex;
    align-items: center;
    width: 100%;
    overflow: hidden;

    &.checkbox-cell {
      cursor: default;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    &.first {
      display: flex;
      gap: 1rem;
    }

    .video-component {
      min-width: 4rem;
      min-height: 4rem;
      max-width: 4rem;
      max-height: 4rem;
    }

    .video-title {
      overflow: hidden;
      display: grid;
      grid-template-columns: 100%;
    }
  }

  .url-cell {
    font-size: 14px;
    :deep(.ellipsis) {
      max-width: 185px;
    }
  }

  .url-disabled {
    color: $disabledText;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions-cell {
    justify-content: center;
  }

  .approve-btn {
    cursor: pointer;
    font-weight: 700;
  }
}
</style>
